import React, { useEffect, useMemo } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { Text } from '@interco/inter-ui/components/Text'
import { Colors } from '@utils/Colors'
import { ColorWrapper, TextParagraph } from '@atoms'
import { BottomSheet } from '@interco/inter-ui/components/BottomSheet'
import { Button } from '@interco/inter-ui/components/Button'
import PdfFile from '@interco/icons/core/files/ic_file_pdf'
import ChevronRight from '@interco/icons/core/action-navigation/ic_chevron_right'
import routes from '@routes/routes'
import { marginAcceptRequest } from '@store/refinanciamento/marginAcceptResult/actions'
import { getDadosPessoaisRequest } from '@store/refinanciamento/dadosPessoais/actions'
import { setBottomsheetOrigin, trackingRefin } from '@store/refinanciamento/simulacao/actions'
import { ApplicationState } from '@store/types'
import { BridgeService } from '@services/bridge'
import { StepRefin } from '@utils/enums'
import Tag from '@utils/Tag'
import { getNextRoute } from '@store/refinanciamento/dadosPessoais/utils'
import { BottomsheetConvenioProps, ConvenioConfig } from '@utils/types'

import { BoxCustom, Line } from './styles'

const getConvenioConfig = (convenioName: string): ConvenioConfig => {
  const configs: Record<string, ConvenioConfig> = {
    inss: {
      termoLabel: 'Termo de autorização INSS',
      termoRoute: routes.AUTORIZACAO_INSS,
    },
    ctps: {
      termoLabel: 'Termo de autorização',
      termoRoute: routes.AUTORIZACAO_CTPS,
    },
  }

  const key = convenioName?.toLowerCase?.() ?? ''

  return configs[key] ?? configs.inss
}

const BottomsheetConvenio = ({ show, setShow }: BottomsheetConvenioProps) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { loading: loadingMargin } = useSelector(
    (state: ApplicationState) => state.refinanciamento.marginAcceptResult,
  )
  const { loadingSimulacao, errorSimulacao } = useSelector(
    (state: ApplicationState) => state.refinanciamento.simulacao,
  )
  const { loading: loadingCustomerData } = useSelector(
    (state: ApplicationState) => state.refinanciamento.dadosPessoais,
  )
  const showErrorState = useSelector((state: ApplicationState) => state.ui.error.showError)
  const contratosSelecionados = useSelector(
    (state: ApplicationState) => state.refinanciamento.contratos.contratosSelecionados,
  )
  const { convenioName } = useSelector((state: ApplicationState) => state.refinanciamento.contratos)
  const { isApp } = useSelector((state: ApplicationState) => state.ui.navigation)

  const config = useMemo(() => getConvenioConfig(convenioName), [convenioName])

  const isLoading = Boolean(loadingMargin || loadingSimulacao || loadingCustomerData)

  useEffect(() => {
    if (!show) return

    BridgeService.tac({
      name: `bottomsheet_${convenioName}`,
      actions: [`abrir_bottomsheet_${convenioName}`],
    })

    if (isApp) {
      Tag.enviarTagueamentoApp({
        screen: 'clt:refin:term',
        ref_type: '',
        content_action: 'Dado carregado',
        action_id: '',
        section: 'term',
        sub_section: '',
        step: '',
        scenario: 'Aceite termo',
        ref_figma: '2',
      })
    } else {
      Tag.enviarTagueamentoWeb({
        element_action: 'data_loaded',
        element_name: '',
        c_page: window.location.href,
        redirect_url: '',
        section_name: 'payrollloan:refin:term',
        section: 'term',
        sub_section: '',
        step: '',
        scenario: 'Aceite termo',
      })
    }
  }, [show, convenioName, isApp])

  useEffect(() => {
    if (errorSimulacao || showErrorState) {
      setShow(false)
    }
  }, [errorSimulacao, showErrorState, setShow])

  const handleClose = () => {
    BridgeService.tic({
      name: `btn_fechar_${convenioName}`,
      action: `fechar_bottomsheet_${convenioName}`,
    })

    if (isApp) {
      Tag.enviarTagueamentoApp({
        screen: 'clt:refin:term',
        ref_type: 'botao',
        content_action: 'Toque',
        action_id: 'Fechar',
        section: 'term',
        sub_section: '',
        step: '',
        scenario: 'Aceite termo',
        ref_figma: '2',
      })
    } else {
      Tag.enviarTagueamentoWeb({
        element_action: 'click button',
        element_name: 'Fechar',
        c_page: window.location.href,
        redirect_url: '',
        section_name: 'payrollloan:refin:term',
        section: 'term',
        sub_section: '',
        step: '',
        scenario: 'Aceite termo',
      })
    }

    setShow(false)
  }

  const handleViewTermo = () => {
    dispatch(trackingRefin(StepRefin.CLICK_CONVENIO_VISUALIZAR_TERMO))
    BridgeService.tic({
      name: `btn_termo_autorizacao_${convenioName}`,
      action: `visualizar_termo_${convenioName}`,
    })

    if (isApp) {
      Tag.enviarTagueamentoApp({
        screen: 'clt:refin:term',
        ref_type: 'botao',
        content_action: 'Toque',
        action_id: 'Continuar',
        section: 'term',
        sub_section: '',
        step: '',
        scenario: 'Aceite termo',
        ref_figma: '2',
      })
    } else {
      Tag.enviarTagueamentoWeb({
        element_action: 'click button',
        element_name: 'Continuar',
        c_page: window.location.href,
        redirect_url: config.termoRoute,
        section_name: 'payrollloan:refin:term',
        section: 'term',
        sub_section: '',
        step: '',
        scenario: 'Aceite termo',
      })
    }

    navigate(config.termoRoute)
  }

  const handleContinue = () => {
    dispatch(trackingRefin(StepRefin.CLICK_CONVENIO_CONTINUAR))
    BridgeService.tic({
      name: `btn_continuar_${convenioName}`,
      action: `continuar_termo_${convenioName}`,
    })

    if (isApp) {
      Tag.enviarTagueamentoApp({
        screen: 'clt:refin:term',
        ref_type: 'botao',
        content_action: 'Toque',
        action_id: 'Continuar',
        section: 'term',
        sub_section: '',
        step: '',
        scenario: 'Aceite termo',
        ref_figma: '2',
      })
    } else {
      Tag.enviarTagueamentoWeb({
        element_action: 'click button',
        element_name: 'Continuar',
        c_page: window.location.href,
        redirect_url: routes.CARREGAMENTO_SIMULACAO,
        section_name: 'payrollloan:refin:term',
        section: 'term',
        sub_section: '',
        step: '',
        scenario: 'Aceite termo',
      })
    }

    dispatch(setBottomsheetOrigin(true))

    if (isApp) {
      dispatch(
        marginAcceptRequest({
          callback: () => {
            setShow(false)
            navigate(routes.CARREGAMENTO_SIMULACAO)
          },
          selectedContracts: contratosSelecionados?.map((c) => c.numeroContrato) || [],
          convenioName,
        }),
      )
    } else {
      dispatch(
        marginAcceptRequest({
          callback: () => {
            dispatch(
              getDadosPessoaisRequest({
                callback: () => {
                  setShow(false)
                  navigate(getNextRoute())
                },
              }),
            )
          },
          selectedContracts: contratosSelecionados?.map((c) => c.numeroContrato) || [],
          convenioName,
        }),
      )
    }
  }

  if (!show) return null

  return (
    <BottomSheet onClose={handleClose} noSwipe>
      <Line>
        <ColorWrapper fontWeight="600">
          <TextParagraph variant="headline-h3">Autorizar termo</TextParagraph>
        </ColorWrapper>
        <ColorWrapper fontWeight="700" color={Colors.PRIMARY500} onClick={handleClose}>
          <TextParagraph variant="body-3">Fechar</TextParagraph>
        </ColorWrapper>
      </Line>

      <ColorWrapper color={Colors.GRAY500} margin="0 auto 24px auto">
        <TextParagraph variant="caption-1">
          Essa autorização nos ajuda a oferecer a melhor condição possível pra você.
        </TextParagraph>
      </ColorWrapper>

      <BoxCustom onClick={handleViewTermo} actived={false}>
        <Text
          variant="body-3"
          style={{
            width: '90%',
            color: Colors.GRAY500,
            fontWeight: 700,
            alignContent: 'flex-start',
          }}
        >
          <PdfFile height={24} width={24} color={Colors.GRAY500} style={{ marginRight: '16px' }} />
          {config.termoLabel}
        </Text>
        <ChevronRight height={24} width={24} color={Colors.PRIMARY500} />
      </BoxCustom>

      <div style={{ margin: '24px 0 0 0 ' }}>
        <Text variant="caption-1">Ao continuar, você declara que aceita o termo acima.</Text>
        <Button
          fullWidth
          style={{ marginTop: '8px' }}
          isLoading={isLoading}
          disabled={isLoading}
          onClick={handleContinue}
        >
          Continuar
        </Button>
      </div>
    </BottomSheet>
  )
}

export default BottomsheetConvenio
